import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import GoogleIcon from 'Components/Icons/GoogleIcon';
import { useAuth } from 'Hooks/useAuth';
import { useNotification } from 'Hooks/useNotification';
import ButtonAppBar from 'Layouts/ButtonAppBar/ButtonAppBar';
import { uploadService } from 'Services/uploadService';
import { userService } from 'Services/userService';
import { UserProfile } from 'Types/api';

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const Profile = () => {
  const { email, hasGoogleLinked, isOAuthEnabled } = useAuth();
  const { showNotification } = useNotification();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(undefined);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const data = await userService.getProfile();
        setProfile(data);
        setDisplayName(data.displayName || '');
        setBio(data.bio || '');
        setAvatarUrl(data.avatarUrl);
        setAvatarPreview(data.avatarUrl || null);
      } catch {
        setError('Failed to load profile');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, []);

  const handleAvatarSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setUploadError('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setUploadError('Image must be smaller than 5MB');
      return;
    }

    setUploadError(null);
    setIsUploading(true);

    const oldAvatarUrl = avatarUrl;

    try {
      // Show preview immediately
      const reader = new FileReader();
      reader.onload = (event) => {
        setAvatarPreview(event.target?.result as string);
      };
      reader.readAsDataURL(file);

      // Upload to S3
      const url = await uploadService.uploadImage(file);
      setAvatarUrl(url);

      // Delete old avatar after successful upload
      if (oldAvatarUrl) {
        uploadService.deleteImage(oldAvatarUrl).catch(() => void 0);
      }
    } catch {
      setUploadError('Failed to upload image. Please try again.');
      setAvatarPreview(oldAvatarUrl || null);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveAvatar = () => {
    if (avatarUrl) {
      uploadService.deleteImage(avatarUrl).catch(() => void 0);
    }
    setAvatarUrl(undefined);
    setAvatarPreview(null);
    setUploadError(null);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updatedProfile = await userService.updateProfile({
        displayName: displayName.trim() || undefined,
        avatarUrl,
        bio: bio.trim() || undefined,
      });
      setProfile(updatedProfile);
      showNotification('Profile updated successfully!', 'success');
    } catch {
      showNotification('Failed to update profile. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const hasChanges =
    profile &&
    (displayName !== (profile.displayName || '') ||
      bio !== (profile.bio || '') ||
      avatarUrl !== profile.avatarUrl);

  if (isLoading) {
    return (
      <>
        <ButtonAppBar />
        <Box sx={{ width: '100%' }}>
          <LinearProgress color='secondary' />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <ButtonAppBar />
        <Container maxWidth='sm'>
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant='h6' color='error'>
              {error}
            </Typography>
          </Box>
        </Container>
      </>
    );
  }

  return (
    <>
      <ButtonAppBar />
      <Container maxWidth='sm'>
        <Box component='main' sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
          <Typography variant='h4' component='h1' gutterBottom>
            My Profile
          </Typography>

          <Paper sx={{ p: 3, mt: 2 }}>
            {/* Avatar Section */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              <Box sx={{ position: 'relative' }}>
                <Avatar
                  src={avatarPreview || undefined}
                  sx={{ width: 120, height: 120, fontSize: '3rem' }}
                >
                  {displayName?.[0]?.toUpperCase() || email?.[0]?.toUpperCase()}
                </Avatar>
                <input
                  ref={fileInputRef}
                  type='file'
                  accept='image/jpeg,image/png,image/gif,image/webp'
                  style={{ display: 'none' }}
                  onChange={handleAvatarSelect}
                  disabled={isUploading}
                />
                <Tooltip title='Change photo'>
                  <IconButton
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      bgcolor: 'background.paper',
                      border: '2px solid',
                      borderColor: 'divider',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                    size='small'
                  >
                    {isUploading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <CameraAltIcon fontSize='small' />
                    )}
                  </IconButton>
                </Tooltip>
              </Box>
              {avatarPreview && (
                <Button
                  size='small'
                  color='error'
                  startIcon={<DeleteIcon />}
                  onClick={handleRemoveAvatar}
                  disabled={isUploading}
                  sx={{ mt: 1 }}
                >
                  Remove photo
                </Button>
              )}
              {uploadError && (
                <Alert severity='error' sx={{ mt: 1, width: '100%' }}>
                  {uploadError}
                </Alert>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Profile Fields */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label='Email'
                value={email}
                disabled
                fullWidth
                helperText='Email cannot be changed'
              />

              <TextField
                label='Display Name'
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                fullWidth
                placeholder='How you want to be known'
                inputProps={{ maxLength: 50 }}
                helperText={`${displayName.length}/50 characters`}
              />

              <TextField
                label='Bio'
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                fullWidth
                multiline
                rows={3}
                placeholder='Tell others about yourself and your cooking style'
                inputProps={{ maxLength: 200 }}
                helperText={`${bio.length}/200 characters`}
              />
            </Box>

            {/* Connected Accounts Section */}
            {isOAuthEnabled && (
              <>
                <Divider sx={{ my: 3 }} />
                <Typography variant='subtitle1' fontWeight={600} gutterBottom>
                  Connected Accounts
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <GoogleIcon />
                    <Typography>Google</Typography>
                  </Box>
                  {hasGoogleLinked ? (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        color: 'success.main',
                      }}
                    >
                      <CheckCircleIcon fontSize='small' />
                      <Typography variant='body2' color='success.main'>
                        Connected
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant='body2' color='text.secondary'>
                      Not connected
                    </Typography>
                  )}
                </Box>
                {!hasGoogleLinked && (
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ mt: 1, display: 'block' }}
                  >
                    Sign out and use &quot;Continue with Google&quot; to link your Google account.
                  </Typography>
                )}
              </>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                variant='contained'
                color='primary'
                startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={isSaving || !hasChanges}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </Paper>
        </Box>
      </Container>
    </>
  );
};

export default Profile;
